import { useCallback, useEffect, useRef, useState } from "react";
import { AlertTriangle, CheckCircle, X } from "lucide-react";

export type ToastTone = "success" | "error";
export type Notify = (tone: ToastTone, text: string) => void;

interface ToastMessage {
    id: number;
    tone: ToastTone;
    text: string;
}

/** Minimal toast queue for the Mobile area (the console has no shared toast system yet). */
export function useToasts() {
    const [toasts, setToasts] = useState<ToastMessage[]>([]);
    const nextId = useRef(1);
    const timers = useRef<number[]>([]);

    useEffect(() => {
        const pending = timers.current;
        return () => pending.forEach((t) => window.clearTimeout(t));
    }, []);

    const dismiss = useCallback((id: number) => setToasts((all) => all.filter((t) => t.id !== id)), []);

    const notify = useCallback<Notify>(
        (tone, text) => {
            const id = nextId.current++;
            setToasts((all) => [...all, { id, tone, text }]);
            timers.current.push(window.setTimeout(() => dismiss(id), tone === "error" ? 10_000 : 7_000));
        },
        [dismiss]
    );

    return { toasts, notify, dismiss };
}

export function ToastViewport({ toasts, onDismiss }: { toasts: ToastMessage[]; onDismiss: (id: number) => void }) {
    return (
        <div
            className="pointer-events-none fixed bottom-14 right-4 z-50 flex w-full max-w-sm flex-col gap-2"
            aria-live="polite"
        >
            {toasts.map((t) => (
                <div
                    key={t.id}
                    role="status"
                    className={`pointer-events-auto flex items-start gap-2 rounded-lg border bg-background p-3 text-sm shadow-lg ${
                        t.tone === "error" ? "border-destructive/50" : ""
                    }`}
                >
                    {t.tone === "error" ? (
                        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" aria-hidden />
                    ) : (
                        <CheckCircle className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600 dark:text-emerald-400" aria-hidden />
                    )}
                    <p className="flex-1">{t.text}</p>
                    <button
                        type="button"
                        onClick={() => onDismiss(t.id)}
                        className="rounded p-0.5 text-muted-foreground hover:bg-accent"
                        aria-label="Dismiss notification"
                    >
                        <X className="h-3.5 w-3.5" />
                    </button>
                </div>
            ))}
        </div>
    );
}
